import {ActionTree, ActionContext} from 'vuex';
import CatalogService from "@/services/CatalogService";

import {RootState} from '@/store';
import {State} from './state';
import {Mutations} from './mutations';
import {FilterMutationTypes} from './mutation-types';
import {FilterActionTypes} from './action-types';
import {ProductActionTypes} from './../product/action-types';

import {
    FilterControlIdentifier,
    FilterData,
    FilterSlideData,
    FilterSlideIdentifier,
    ProductsLoadOptions
} from "@/types";

type AugmentedActionContext = {
    commit<K extends keyof Mutations>(
        key: K,
        payload?: Parameters<Mutations[K]>[1],
    ): ReturnType<Mutations[K]>;
} & Omit<ActionContext<State, RootState>, 'commit'>

export interface Actions {
    [FilterActionTypes.SET_FILTER](
        {commit}: AugmentedActionContext,
        data: FilterData
    ): void;

    [FilterActionTypes.SORT_PRODUCTS](
        {commit}: AugmentedActionContext,
        sortType: string
    ): void;

    [FilterActionTypes.CHANGE_PARAMETER](
        {commit}: AugmentedActionContext,
        controlData?: FilterControlIdentifier | null
    ): void;

    [FilterActionTypes.CHANGE_SLIDE_VALUE](
        {commit}: AugmentedActionContext,
        slideValue: FilterSlideIdentifier | null
    ): void;

    [FilterActionTypes.CLEAR_FILTRATION](
        {commit}: AugmentedActionContext,
        controlData?: FilterControlIdentifier | FilterSlideIdentifier | null
    ): void;

    [FilterActionTypes.PROCESS_FILTER](
        {dispatch}: AugmentedActionContext
    ): void;

    [FilterActionTypes.REFETCH_FILTER](
        {commit}: AugmentedActionContext,
        options: ProductsLoadOptions
    ): Promise<void>;

    [FilterActionTypes.SET_IS_OPENED_FILTER_MODAL](
        {commit}: AugmentedActionContext,
        open: boolean
    ): void;
}

export const actions: ActionTree<State, RootState> & Actions = {
    [FilterActionTypes.SET_FILTER]({commit}, data: FilterData) {
        commit(FilterMutationTypes.SET_DATA, data);
    },
    [FilterActionTypes.SORT_PRODUCTS]({commit, dispatch}, sortType: string) {
        commit(FilterMutationTypes.SET_SORT, sortType);
        dispatch(FilterActionTypes.PROCESS_FILTER);
    },
    [FilterActionTypes.CHANGE_PARAMETER]({commit, dispatch, getters}, controlData: FilterControlIdentifier) {
        const control = getters.getFilterControl(controlData);
        if (control) {
            commit(FilterMutationTypes.CHANGE_PARAMETER, control);
            dispatch(FilterActionTypes.PROCESS_FILTER)
        }
    },
    [FilterActionTypes.CHANGE_SLIDE_VALUE]({commit, dispatch, getters}, slideData: FilterSlideIdentifier) {
        const slide: FilterSlideData | undefined = getters.getFilterSlide(slideData);
        if (slide && ((slideData.type === slide.nameMin && slideData.value !== slide.minValue)
            || (slideData.type === slide.nameMax && slideData.value !== slide.maxValue))) {
            commit(FilterMutationTypes.CHANGE_SLIDE_VALUE, {...slideData, control: slide});
            dispatch(FilterActionTypes.PROCESS_FILTER);
        }
    },
    [FilterActionTypes.CLEAR_FILTRATION]({commit, dispatch, getters}, controlData: FilterControlIdentifier | FilterSlideIdentifier) {
        if (!controlData) {
            commit(FilterMutationTypes.CLEAR_CHECKS, getters.getFilterSelection);
            commit(FilterMutationTypes.CLEAR_SLIDES, getters.getChangedSlides);
        } else if (Object.prototype.hasOwnProperty.call(controlData, 'name')) {
            commit(FilterMutationTypes.CLEAR_CHECKS, [getters.getFilterControl(controlData)]);
        } else if (Object.prototype.hasOwnProperty.call(controlData, 'type')) {
            commit(FilterMutationTypes.CLEAR_SLIDES, [getters.getFilterSlide(controlData)]);
        }
        dispatch(FilterActionTypes.PROCESS_FILTER);
    },
    [FilterActionTypes.PROCESS_FILTER]({dispatch, getters}) {
        dispatch(FilterActionTypes.REFETCH_FILTER, {
            url: getters.getBaseUrl,
            queryParams: getters.getUrlQuery
        });
    },
    async [FilterActionTypes.REFETCH_FILTER]({commit, dispatch}, options: ProductsLoadOptions) {
        dispatch(ProductActionTypes.SET_LOADING);
        return CatalogService.getFilterData(options.url, options.queryParams)
            .then((response: FilterData) => {
                dispatch(FilterActionTypes.SET_FILTER, response);
                dispatch(ProductActionTypes.SET_PRODUCTS, response);
            })
            .catch(error => {
                console.log(error);
            })
            .finally(() => {
                dispatch(ProductActionTypes.SET_LOADING);
                CatalogService.writeHistory(options.url, options.queryParams);
            })
    },
    [FilterActionTypes.SET_IS_OPENED_FILTER_MODAL]({commit}, open) {
        commit(open ? FilterMutationTypes.OPEN_FILTER_MODAL : FilterMutationTypes.CLOSE_FILTER_MODAL);
    },
};